/* eslint-disable jsx-a11y/img-redundant-alt */
import React, {useState, useEffect, useRef, useCallback} from "react"
import {useTranslation} from 'react-i18next'
import "../../css/Buttons.css"
import "../../css/Transitions.css"
import {Modal} from "react-bootstrap"
import {CSSTransition} from "react-transition-group"
import IconButton from "../buttons/IconButton"
import {ReactComponent as HomeIcon} from '../../icon/home.svg'
import {ReactComponent as ShareIcon} from '../../icon/share.svg'
import {ReactComponent as GalleryIcon} from '../../icon/gallery.svg'
import SecondaryIconButton from "../buttons/SecondaryIconButton"
import Config from "../../library/Config"
import {fetchUpload, Gallery, GalleryPrivacy, ShareSheet, trackView, Upload, UploadLayout} from "@simplebooth/webkit"

interface PicViewProps {
  uploadCode: string
  presentation: string
  config: Config
  reset: (fullReset: boolean) => void
}

function PicView({uploadCode, presentation, config, reset}: PicViewProps) {
  const {t} = useTranslation()

  const [upload, setUpload] = useState<Upload | undefined>()
  const [gallery, setGallery] = useState<Gallery | undefined>()
  const [showShare, setShowShare] = useState(false)
  const [didShare, setDidShare] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const buttonPressTimer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    fetchUpload(uploadCode, (uploadResource) => {
      const newUpload = Upload.fromResource(uploadResource)
      setUpload(newUpload)

      trackView(newUpload.code)

      if (uploadResource._embedded && uploadResource._embedded.gallery) {
        const newGallery = Gallery.fromResource(uploadResource._embedded.gallery)
        setGallery(newGallery)
      }
    }, (errorCode: number) => {
      setError(`${errorCode} error`)
    })
  }, [uploadCode])

  const handleShareClick = useCallback(() => {
    setShowShare(true)
  }, [])

  const hideShareSheet = useCallback(() => {
    setShowShare(false)
    setDidShare(true)
  }, [])

  const handleTakeAnother = useCallback(() => {
    reset(false)
  }, [reset])

  const handleTouchStart = useCallback(() => {
    buttonPressTimer.current = setTimeout(() => {
      setDidShare(true)
    }, 1500)
  }, [])

  const handleTouchEnd = useCallback(() => {
    clearTimeout(buttonPressTimer.current)
  }, [])

  const theme = config.theme

  if (error) {
    return (
      <>
        <h3 style={{margin: "30px 0"}}>{error}</h3>
        <button className="bigButton" style={theme.bigButtonStyle} onClick={() => reset(true)}>
          {t("pic.home")}
        </button>
      </>
    )
  }

  if (!upload || !gallery) {
    return <></>
  }

  if (upload.deleted) {
    return (
      <>
        <h3 style={{margin: "30px 0"}}>{t("pic.deleted")}</h3>
        <button className="bigButton" style={theme.bigButtonStyle} onClick={() => reset(true)}>
          {t("pic.home")}
        </button>
      </>
    )
  }

  return (
    <>
      <CSSTransition in={config.finishedPicMessage != null && presentation === "new"} timeout={500} classNames="fade" unmountOnExit>
        <div className="basicContainer">
          <h3 style={{color: theme.textColor, marginTop: "20px"}}>{config.finishedPicMessage}</h3>
        </div>
      </CSSTransition>

      <CSSTransition in appear timeout={500} classNames="camera" unmountOnExit>
        <div className="boothContainer">
          <div className="square">
            <img
              className="fill"
              src={upload.media.original} alt="Your finished photo"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            />
          </div>
        </div>
      </CSSTransition>

      <CSSTransition in={presentation === "new"} appear timeout={1000} classNames="fadeIn" unmountOnExit>
        <div className="buttonRow">
          {config.allowSharing && !didShare ?
            <button className="bigButton" style={theme.bigButtonStyle} onClick={handleShareClick}>
              {config.buttons.share ? config.buttons.share.title : t("pic.shareIt")}
            </button>
            :
            <>
              {gallery.privacy === GalleryPrivacy.PUBLIC && gallery.links.webSelf &&
                <SecondaryIconButton id="viewGalleryButton"
                                     theme={theme}
                                     text={t("pic.gallery")}
                                     onPress={() => {
                                       gallery?.links.webSelf && window.open(gallery.links.webSelf, "_blank")
                                     }}>
                  <GalleryIcon style={theme.svgStyleAlternate}/>
                </SecondaryIconButton>
              }

              <IconButton theme={theme} onPress={handleTakeAnother}>
                <HomeIcon style={theme.svgStyle}/>
              </IconButton>

              {config.allowSharing &&
                <SecondaryIconButton theme={theme} text={t("pic.share")} onPress={handleShareClick}>
                  <ShareIcon style={theme.svgStyleAlternate}/>
                </SecondaryIconButton>
              }

              {config.buttons.postShare &&
                <div className="mt-4">
                  <button className="bigButton" style={theme.textStyle} onClick={() => {
                    if (config.buttons.postShare && config.buttons.postShare.url) {
                      window.location.href = config.buttons.postShare.url
                    }
                  }}>
                    {config.buttons.postShare.title}
                  </button>
                </div>
              }
            </>
          }
        </div>
      </CSSTransition>

      {presentation === "view" &&
        <div className="buttonRow">
          {config.buttons.viewPic ?
            <button className="bigButton" style={theme.bigButtonStyle} onClick={() => {
              if (config.buttons.viewPic && config.buttons.viewPic.url) {
                window.location.href = config.buttons.viewPic.url
              }
            }}>
              {config.buttons.viewPic.title}
            </button>
            :
            <button className="bigButton" style={theme.bigButtonStyle} onClick={() => reset(true)}>
              {t("pic.participate")}
            </button>
          }
        </div>
      }

      <Modal id="shareSheet" show={showShare} centered onHide={hideShareSheet}>
        <Modal.Body className="p-4">
          <ShareSheet
            oneColumn
            heading={upload.layout === UploadLayout.VIDEO ? t("shareSheet.headings.video") : t("shareSheet.headings.photo")}
            upload={upload}
            hashtags={gallery.hashtag}
            shareOptions={gallery.getShareOptions()}
            onHide={hideShareSheet}
          />
        </Modal.Body>
      </Modal>
    </>
  )
}

export default PicView
